/**
 * Integer money math for the pricing engine.
 * Money = integer micro-dollars (µ$, 1e-6 dollars). Percentages = basis points.
 * No floats: all rounding is explicit, HALF_EVEN, and happens exactly once
 * per price line (spec §6). BigInt is used internally so products up to
 * ~1e15 stay exact; inputs/outputs stay safe Numbers.
 */

export type Micros = number;       // integer micro-dollars
export type Cents = number;        // integer cents
export type BasisPoints = number;  // integer basis points (800 = 8.00%)
export type Dollars = number;      // 2-decimal dollars, as productDb stores them

export function assertSafeInt(n: number, label: string): void {
  if (!Number.isSafeInteger(n)) {
    throw new Error(`${label} must be a safe integer, got ${n} (unsafe)`);
  }
}

/** HALF_EVEN(Σ(aᵢ·bᵢ) / den), all-integer via BigInt throughout. The single
 *  home of banker's rounding (part-3 consolidation): mulDivHalfEven is the
 *  1-pair case; solve/backDerive's fused two-term numerators are 2-pair calls;
 *  engine's uplift+exempt split is a 2-pair call. Only the post-division
 *  quotient (safe magnitude) converts back to Number. Guards the numerator
 *  BEFORE dividing (not just the final quotient): a numerator can round down
 *  to a safe quotient while still being too large to trust bit-for-bit (e.g.
 *  27021597764222974/3 lands exactly on MAX_SAFE_INTEGER), so this mirrors
 *  the old mulDivHalfEven's pre-division "product unsafe" check. */
export function dotDivHalfEven(pairs: Array<[number, number]>, den: number): number {
  assertSafeInt(den, 'den');
  if (den <= 0) throw new Error(`denominator must be positive, got ${den}`);
  let num = 0n;
  for (const [a, b] of pairs) {
    assertSafeInt(a, 'term factor a'); assertSafeInt(b, 'term factor b');
    num += BigInt(a) * BigInt(b);
  }
  const d = BigInt(den);
  const absNum = num < 0n ? -num : num;
  if (absNum > BigInt(Number.MAX_SAFE_INTEGER) * d) {
    throw new Error(`numerator unsafe: |Σ(aᵢ·bᵢ)|=${absNum} exceeds MAX_SAFE_INTEGER·den (den=${den})`);
  }
  const q = num / d;
  const r = num % d;
  const twice = 2n * (r < 0n ? -r : r);
  let out = q;
  if (twice > d || (twice === d && q % 2n !== 0n)) out += num < 0n ? -1n : 1n;
  const result = Number(out);
  assertSafeInt(result, 'dotDivHalfEven result');
  return result;
}

/** (value * num) / den with HALF_EVEN rounding, all-integer. Delegates to
 *  dotDivHalfEven (the 1-pair case) but keeps its own labeled asserts on
 *  `value`/`num`/`den` so error messages stay specific to this call site
 *  (a golden test pins the 'value' label — see backDerive.test.ts). */
export function mulDivHalfEven(value: number, num: number, den: number): number {
  assertSafeInt(value, 'value'); assertSafeInt(num, 'num'); assertSafeInt(den, 'den');
  return dotDivHalfEven([[value, num]], den);
}

/** µ$ -> cents, HALF_EVEN. Used ONLY at the one per-line rounding point. */
export function microsToCentsHalfEven(micros: number): Cents {
  return mulDivHalfEven(micros, 1, 10000);
}

/**
 * Cents -> dollars. The ONLY permitted site of this conversion, mirroring how
 * dotDivHalfEven is the only home of banker's rounding.
 *
 * Money representations in this codebase:
 *   integer µ$    catalog cost rows, snapshots — costs are multiplied by qty
 *                 and by (1 + integration%) BEFORE rounding, so sub-cent
 *                 precision is required
 *   basis points  integrationBp, hwDiscountBp
 *   integer cents transient, inside deriveHwPricing only — the result of the
 *                 single HALF_EVEN rounding per price line
 *   dollars       PriceEdition.sheet, the price book, productDb — what a price
 *                 IS, and what a human reads
 *
 * toFixed(2) then Number() rather than `cents / 100` so the result is the
 * double nearest the 2-decimal value — the same one JSON parsing of
 * productDb's `40305.51` yields. That identity is what makes the parity gate a
 * direct comparison.
 */
export function centsToDollars(cents: Cents): Dollars {
  assertSafeInt(cents, 'cents');
  return Number((cents / 100).toFixed(2));
}

/** µ$ -> dollars, via the single HALF_EVEN cent rounding. Was duplicated
 *  verbatim in CatalogPage.jsx and ReviewPanel.jsx.
 *
 *  Component/chassis/etc. rows are authored in integer µ$ (costDraft.ts:26).
 *  Composed from this module's two canonical steps — `microsToCentsHalfEven`
 *  (the one permitted µ$->cents rounding) then `centsToDollars` (the one
 *  permitted cents->dollars conversion) — rather than a bare `/1e6`. */
export function microsToDollars(value: string | number): Dollars {
  return centsToDollars(microsToCentsHalfEven(Number(value)));
}
